import React, { useState } from 'react';
import { AI_PRESETS } from '../../../services/aiDesignService';

const PRESET_ICONS = {
  whiten: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
    </svg>
  ),
  straighten: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  ),
  fullMakeover: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
    </svg>
  ),
  closeGaps: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
    </svg>
  ),
};

const AiDesignPanel = ({
  selectedPhoto,
  onGenerate,
  aiResult,
  aiLoading,
  aiError,
  showComparison,
  onToggleComparison,
  generationsRemaining,
}) => {
  const [selectedPreset, setSelectedPreset] = useState('fullMakeover');
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const handleGenerate = () => {
    if (useCustom) {
      onGenerate(null, customPrompt);
    } else {
      onGenerate(selectedPreset, null);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-gray-800 mb-3">AI Smile Design</h3>

      {!selectedPhoto && (
        <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-[11px] text-amber-700">
            Load a patient photo first to use AI design.
          </p>
        </div>
      )}

      {/* Preset Selection */}
      <p className="text-xs text-gray-500 mb-2">Choose an enhancement:</p>
      <div className="space-y-1.5 mb-4">
        {AI_PRESETS.map(preset => (
          <button
            key={preset.id}
            onClick={() => { setSelectedPreset(preset.id); setUseCustom(false); }}
            disabled={aiLoading}
            className={`w-full text-left p-2.5 rounded-lg border transition ${
              selectedPreset === preset.id && !useCustom
                ? 'border-purple-400 bg-purple-50'
                : 'border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`${
                selectedPreset === preset.id && !useCustom ? 'text-purple-600' : 'text-gray-400'
              }`}>
                {PRESET_ICONS[preset.id]}
              </span>
              <div>
                <span className="text-xs font-medium text-gray-800 block">{preset.name}</span>
                <span className="text-[10px] text-gray-500">{preset.description}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Custom Prompt Toggle */}
      <div className="mb-4">
        <button
          onClick={() => setUseCustom(!useCustom)}
          className={`text-[11px] font-medium mb-1.5 ${
            useCustom ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {useCustom ? '\u25BE Custom prompt' : '\u25B8 Custom prompt'}
        </button>
        {useCustom && (
          <textarea
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="Describe the smile changes you want..."
            rows={3}
            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
        )}
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={aiLoading || !selectedPhoto || (useCustom && !customPrompt.trim())}
        className="w-full py-2.5 px-4 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {aiLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            Generating...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
            Generate AI Design
          </>
        )}
      </button>

      {/* Generations remaining */}
      {generationsRemaining !== null && generationsRemaining !== undefined && (
        <p className="text-[10px] text-gray-400 text-center mt-1.5">
          {generationsRemaining} generations remaining this month
        </p>
      )}

      {/* Error */}
      {aiError && (
        <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-[11px] text-red-700">{aiError}</p>
        </div>
      )}

      {/* Result Actions */}
      {aiResult && !aiLoading && (
        <div className="mt-4 space-y-2">
          <div className="border-t border-gray-100 pt-3">
            <p className="text-[11px] font-medium text-green-700 mb-2">
              AI design generated successfully
            </p>
            <button
              onClick={onToggleComparison}
              className={`w-full py-2 px-3 text-xs font-medium rounded-lg border transition ${
                showComparison
                  ? 'border-blue-400 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {showComparison ? 'Hide Comparison' : 'Compare Before / After'}
            </button>
          </div>
        </div>
      )}

      {/* Info note */}
      <p className="mt-4 text-[10px] text-gray-400 text-center leading-tight">
        AI results are for treatment visualization only. Results may vary from actual outcomes.
      </p>
    </div>
  );
};

export default AiDesignPanel;
